/**
 * Hospital Layout - Command Center Style with Persistent Sidebar
 * Enterprise-grade UI for hospital operations management
 */

import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShieldCheck,
  Zap,
  AlertTriangle,
  Bell,
  Settings,
  User,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Building2,
  FileText,
  Truck,
  Clock,
  Activity,
  Wrench,
  ClipboardCheck,
  Droplets,
  Users,
  Gavel,
  Award,
  ShoppingCart,
  Leaf,
  Wind
} from "lucide-react";
import NotificationCenter from "@/components/NotificationCenter";
import { Home as HomeIcon, ListOrdered } from "lucide-react";

// Navigation items for hospital command center - matches dashboard tabs
const navItems = [
  {
    id: "home",
    label: "Home",
    icon: HomeIcon,
    path: "/hospital/portal",
    tab: "home",
    exact: true,
    description: "Public homepage"
  },
  { 
    id: "dashboard", 
    label: "Dashboard Overview", 
    icon: LayoutDashboard, 
    path: "/hospital/portal?tab=overview",
    tab: "overview"
  },
  {
    id: "my-orders",
    label: "My Orders",
    icon: ListOrdered,
    path: "/hospital/portal?tab=orders",
    tab: "orders"
  },
  { 
    id: "maintenance", 
    label: "Maintenance", 
    icon: Wrench, 
    path: "/hospital/portal?tab=requests",
    tab: "requests"
  },
  {
    id: "backorders",
    label: "Live Backorders",
    icon: Package,
    path: "/hospital/portal?tab=backorders",
    tab: "backorders",
    badge: "live",
    description: "Your backorder requests"
  },
  { 
    id: "quickbids", 
    label: "QuickBids", 
    icon: Gavel, 
    path: "/hospital/portal?tab=quickbids",
    tab: "quickbids",
    description: "Competitive bidding"
  },
  { 
    id: "equipment", 
    label: "Equipment", 
    icon: Package, 
    path: "/hospital/portal?tab=assets",
    tab: "assets"
  },
  { type: "divider" },
  { 
    id: "sign-offs", 
    label: "Work Sign-Offs", 
    icon: ClipboardCheck, 
    path: "/hospital/portal?tab=sign-offs",
    tab: "sign-offs",
    badge: "alerts"
  },
  { 
    id: "water-testing", 
    label: "Microbiology Testing", 
    icon: Droplets, 
    path: "/hospital/portal?tab=water-testing",
    tab: "water-testing"
  },
  { 
    id: "di-tanks", 
    label: "DI Tanks", 
    icon: Droplets, 
    path: "/hospital/portal?tab=di-tanks",
    tab: "di-tanks"
  },
  { type: "divider" },
  { 
    id: "users", 
    label: "Users", 
    icon: Users, 
    path: "/hospital/portal?tab=users",
    tab: "users"
  },
  { 
    id: "analytics", 
    label: "Analytics", 
    icon: Activity, 
    path: "/hospital/portal?tab=analytics",
    tab: "analytics"
  },
  { 
    id: "capabilities", 
    label: "Partner Capabilities", 
    icon: Award, 
    path: "/hospital/capabilities",
    external: true,
    description: "About Sterile Mate"
  },
  { 
    id: "marketplace", 
    label: "Marketplace", 
    icon: ShoppingCart, 
    path: "/hospital/marketplace",
    external: true,
    badge: "new",
    description: "Filmop USA Products"
  },
  { 
    id: "marketplace-janitorial", 
    label: "↳ Janitorial / EVS", 
    icon: Wind, 
    path: "/hospital/marketplace/janitorial",
    external: true,
    description: "ATP, Chemicals, Vacuums, Floor Care, CleanSite"
  },
  { 
    id: "marketplace-green-klean", 
    label: "↳ Green Klean®", 
    icon: Leaf, 
    path: "/hospital/marketplace/green-klean",
    external: true,
    description: "Chlorinated disinfecting tablets"
  },
  { 
    id: "settings", 
    label: "Settings", 
    icon: Settings, 
    path: "/hospital/portal?tab=settings",
    tab: "settings"
  },
];

// Sidebar Component
const Sidebar = ({ collapsed, setCollapsed, hospital, alerts = 0, liveBackorders = 0, onLogout }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get("tab") || "home";

  const isActive = (item) => {
    // External links (like /#quick-quote, /hospital/marketplace) never highlight as active
    if (item.external) return false;
    // For tab-based navigation
    if (item.tab) {
      return currentTab === item.tab;
    }
    // For exact path matching (overview/dashboard)
    if (item.exact) {
      return location.pathname === "/hospital/portal" && !searchParams.get("tab");
    }
    return location.pathname.startsWith(item.path.split("?")[0]);
  };

  const getBadgeContent = (badge) => {
    if (badge === "live" && liveBackorders > 0) {
      return liveBackorders;
    }
    if (badge === "alerts" && alerts > 0) {
      return alerts;
    }
    return null;
  };

  return (
    <aside 
      className={`fixed left-0 top-0 h-full bg-slate-900 transition-all duration-300 z-50 flex flex-col ${
        collapsed ? "w-16" : "w-64"
      }`}
      data-testid="hospital-sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
        {!collapsed && (
          <Link to="/hospital/portal" className="flex items-center gap-2">
            <img 
              src="/images/sterilemate-logo.jpg" 
              alt="SterileMate" 
              className="h-10 w-auto object-contain"
              data-testid="hospital-sidebar-logo"
            />
          </Link>
        )}
        {collapsed && (
          <Link to="/hospital/portal" className="block mx-auto">
            <img 
              src="/images/sterilemate-logo.jpg" 
              alt="SterileMate" 
              className="h-8 w-auto object-contain"
              data-testid="hospital-sidebar-logo-collapsed"
            />
          </Link>
        )}
        <button 
          onClick={() => setCollapsed(!collapsed)}
          className="text-slate-400 hover:text-white p-1 rounded hidden lg:block"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {navItems.map((item, index) => {
            if (item.type === "divider") {
              return (
                <li key={`divider-${index}`} className="my-3">
                  <div className="h-px bg-slate-800 mx-2" />
                </li>
              );
            }

            const Icon = item.icon;
            const active = isActive(item);
            const badgeContent = getBadgeContent(item.badge);
            // Items with a hash anchor (or marked external going to public site) need a full
            // browser navigation so the destination anchor is scrolled into view.
            const isHashLink = item.path?.includes("#");
            const LinkComponent = isHashLink ? "a" : Link;
            const linkProps = isHashLink ? { href: item.path } : { to: item.path };

            return (
              <li key={item.id}>
                <LinkComponent
                  {...linkProps}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors group relative ${
                    active
                      ? "bg-cyan-600 text-white"
                      : "text-slate-400 hover:bg-slate-800 hover:text-white"
                  }`}
                  title={collapsed ? item.label : undefined}
                  data-testid={`nav-${item.id}`}
                >
                  <Icon size={20} strokeWidth={1.5} className="flex-shrink-0" />
                  {!collapsed && (
                    <>
                      <div className="flex-1">
                        <span className="text-sm font-medium">{item.label}</span>
                        {item.description && !active && (
                          <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                        )}
                      </div>
                      {badgeContent && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                          item.badge === "live" ? "bg-emerald-500 text-white animate-pulse" : 
                          "bg-red-500 text-white"
                        }`}>
                          {badgeContent}
                        </span>
                      )}
                    </>
                  )}
                  {collapsed && badgeContent && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {badgeContent > 9 ? "9+" : badgeContent}
                    </span>
                  )}
                </LinkComponent>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Hospital Info & Logout */}
      <div className="border-t border-slate-800 p-3">
        {!collapsed ? (
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-9 h-9 bg-slate-700 rounded-full flex items-center justify-center">
              <Building2 size={18} className="text-slate-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {hospital?.name || hospital?.hospital_name || "Hospital"}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {hospital?.contact_name || hospital?.user_name || ""}
              </p>
            </div>
          </div>
        ) : null}
        <button
          onClick={onLogout}
          className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors ${
            collapsed ? "justify-center" : ""
          }`}
          title="Logout"
          data-testid="logout-btn"
        >
          <LogOut size={18} />
          {!collapsed && <span className="text-sm">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

// Top Header Component
const TopHeader = ({ title, subtitle, actions, collapsed, alerts = 0, token }) => {
  return (
    <header 
      className={`sticky top-0 z-40 bg-white border-b border-slate-200 h-14 sm:h-16 flex items-center justify-between px-4 sm:px-6 transition-all duration-300 ${
        collapsed ? "lg:ml-16" : "lg:ml-64"
      }`}
      data-testid="top-header"
    >
      <div className="ml-10 lg:ml-0">
        <h1 className="text-lg sm:text-xl font-semibold text-slate-900">{title}</h1>
        {subtitle && <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">{subtitle}</p>}
      </div>
      
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Notification Center */}
        <NotificationCenter 
          token={token} 
          userType="hospital"
          onNotificationClick={(notification) => {
            // Handle notification click - could navigate to related item
            console.log("Notification clicked:", notification);
          }}
        />
        
        {/* Custom Actions */}
        {actions}
      </div>
    </header>
  );
};

// Main Layout Export
export default function HospitalLayout({ 
  children, 
  title = "Dashboard", 
  subtitle,
  actions,
  hospital = null,
  alerts = 0,
  liveBackorders = 0,
  onLogout = () => {},
  token = null,
  hideTopHeader = false,
  mainPadding = "p-4 sm:p-6"
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Get token from localStorage if not provided
  const authToken = token || localStorage.getItem("hospital_token");

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Menu Button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-slate-900 text-white rounded-lg"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        data-testid="mobile-menu-btn"
      >
        {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Hidden on mobile unless menu open */}
      <div className={`lg:block ${mobileMenuOpen ? "block" : "hidden"}`}>
        <Sidebar 
          collapsed={collapsed} 
          setCollapsed={setCollapsed} 
          hospital={hospital}
          alerts={alerts}
          liveBackorders={liveBackorders}
          onLogout={onLogout}
        />
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${collapsed ? "lg:ml-16" : "lg:ml-64"}`}>
        {!hideTopHeader && (
          <TopHeader 
            title={title} 
            subtitle={subtitle}
            actions={actions}
            collapsed={collapsed}
            alerts={alerts}
            token={authToken}
          />
        )}
        
        <main className={mainPadding} data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}

export { Sidebar, TopHeader };
